import React, { useEffect, useMemo, useRef, useState } from 'react';

import DataTable from '../shared/data_table';
import Pager from '../shared/pager';
import StatusPill from '../shared/status_pill';
import { CheckChip, FilterBar } from '../shared/controls';
import { EM_DASH, fmt_count } from '../format';
import {
  DEFAULT_TEACHER_SORT,
  GROUP_LABELS,
  GROUP_ONLY_OURS,
  GROUP_ONLY_SHEET,
  NET_BASIS_LABEL,
  NET_BASIS_TEXT,
  column_lines,
  diff_text,
  filter_teachers,
  next_teacher_sort,
  sort_teachers,
  tie_out,
  value_text,
  worst_text
} from './logic';
import { fetch_teacher_lines } from './api';

const PAGE_SIZE = 50;
const signed_class = (n) => (n > 0 ? 'tp-pos' : n < 0 ? 'tp-neg' : undefined);
const one_line = (h) => String(h).replace(/\s+/g, ' ');

// Name, ID and the full UUID (never shortened); the warnings the per-cell list also shows.
const Who = ({ t }) => (
  <div className="tp-c-who">
    <div className="tp-c-name">{t.teacher_name || EM_DASH}</div>
    <div className="tp-c-meta">ID {t.teacher_id}</div>
    {t.teacher_uuid ? <div className="tp-c-meta tp-st-uuid">{t.teacher_uuid}</div> : null}
    {t.ties_out === false ? (
      <div className="tp-c-meta">
        <StatusPill tone="orange">Lines don&apos;t add up</StatusPill>
      </div>
    ) : null}
  </div>
);

// The lines behind one differing cell; the teacher's drill-down is loaded once (see use_lines).
const CellLines = ({ t, c, lines_of, on_download, on_close }) => {
  const uuid = t.teacher_uuid;
  useEffect(() => {
    if (c.from_lines && uuid) lines_of.load(uuid);
  }, [c.from_lines, uuid]); // eslint-disable-line react-hooks/exhaustive-deps
  const entry = uuid ? lines_of.get(uuid) : null;
  let body;
  if (!c.from_lines)
    body = <p className="tp-st-note">This is a total, not from individual classes.</p>;
  else if (!uuid)
    body = <p className="tp-st-note">No teacher UUID, so the classes cannot be loaded.</p>;
  else if (!entry || entry.state === 'loading')
    body = (
      <p className="tp-st-note" role="status">
        Loading the classes…
      </p>
    );
  else if (entry.state === 'error')
    body = (
      <p className="tp-st-note tp-neg" role="alert">
        Could not load the classes.{' '}
        <button type="button" className="tp-link" onClick={() => lines_of.load(uuid, true)}>
          Retry
        </button>
      </p>
    );
  else {
    const lines = column_lines(entry.data, c.fold_keys || c.key, c.line_sign || 1);
    const tie = tie_out(lines, c);
    body = (
      <>
        {lines.length ? (
          <ul className="tp-st-lines-list" aria-label={`Classes behind ${one_line(c.header)}`}>
            {lines.map((l) => (
              <li key={l.key} className="tp-st-line">
                <span className="tp-st-line-hd">
                  <span>{l.time_ist || EM_DASH}</span>
                  <span className={signed_class(l.amount)}>{value_text(l.amount, c)}</span>
                </span>
                <span>{l.label}</span>
                <span className="tp-muted">
                  {[l.status, l.rule_name, l.covered ? 'Covered by Free Pass Day' : '']
                    .filter(Boolean)
                    .join(' · ') || EM_DASH}
                </span>
                {l.explain ? <span className="tp-muted">{l.explain}</span> : null}
                {l.href ? (
                  <a className="tp-link" href={l.href} target="_blank" rel="noopener noreferrer">
                    Open session
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="tp-st-note">No classes feed this column for this month.</p>
        )}
        <p className="tp-st-note tp-st-lines-foot">{tie.text}</p>
        {tie.adds_up ? null : <p className="tp-st-note tp-neg">{tie.mismatch}</p>}
        {lines.length ? (
          <button type="button" className="tp-btn-out" onClick={() => on_download(lines, t, c)}>
            ↓ These lines CSV
          </button>
        ) : null}
      </>
    );
  }
  return (
    <li className="tp-st-lines" aria-label={`Behind ${one_line(c.header)}`}>
      <div className="tp-st-lines-hd">
        <span className="tp-st-tcell-hd">
          {c.from_lines ? `The classes behind ${value_text(c.ours, c)}` : one_line(c.header)}
        </span>
        <button type="button" className="tp-link" onClick={on_close}>
          Close
        </button>
      </div>
      {body}
    </li>
  );
};

// teacher_uuid -> { state, data }; a teacher's lines are fetched once and shared by all its cells.
const use_lines = (month) => {
  const [store, setStore] = useState({});
  const inflight = useRef({});
  const load = (uuid, retry = false) => {
    const cur = store[uuid];
    if (inflight.current[uuid] || (cur && !retry && cur.state !== 'error')) return;
    inflight.current[uuid] = true;
    setStore((s) => ({ ...s, [uuid]: { state: 'loading' } }));
    fetch_teacher_lines(uuid, month)
      .then((data) => setStore((s) => ({ ...s, [uuid]: { state: 'ready', data } })))
      .catch(() => setStore((s) => ({ ...s, [uuid]: { state: 'error' } })))
      .finally(() => {
        inflight.current[uuid] = false;
      });
  };
  return { get: (uuid) => store[uuid], load };
};

const CellVals = ({ c }) => (
  <>
    <span className="tp-st-tcell-hd">{one_line(c.header)}</span>
    <span className="tp-st-tcell-vals">
      <span>
        Your sheet{' '}
        {c.invalid ? (
          <span className="tp-neg">“{c.sheet_text}” is not a number</span>
        ) : (
          value_text(c.sheet, c)
        )}
      </span>
      <span>Ours {value_text(c.ours, c)}</span>
      <span>
        Difference{' '}
        {c.invalid ? (
          EM_DASH
        ) : c.diff ? (
          <span className={signed_class(c.diff)}>{diff_text(c.diff, c)}</span>
        ) : (
          <span className="tp-muted">{value_text(0, c)}</span>
        )}
      </span>
    </span>
  </>
);

// Every compared cell of one teacher, differing ones first; wraps, never scrolls sideways. A
// differing box opens its lines in a full-width panel right below it (one open at a time).
const TeacherCells = ({ t, lines_of, on_download_lines }) => {
  const [open_key, setOpenKey] = useState('');
  return (
    <ul
      className="tp-st-tcells"
      aria-label={`All compared columns for ${t.teacher_name || t.teacher_id}`}
    >
      {t.cells.map((c) => (
        <React.Fragment key={c.key}>
          {c.differs ? (
            <li className="tp-st-tcell-wrap">
              <button
                type="button"
                className="tp-st-tcell is-diff is-button"
                aria-expanded={open_key === c.key}
                onClick={() => setOpenKey((k) => (k === c.key ? '' : c.key))}
              >
                <CellVals c={c} />
              </button>
            </li>
          ) : (
            <li className="tp-st-tcell">
              <CellVals c={c} />
            </li>
          )}
          {open_key === c.key ? (
            <CellLines
              t={t}
              c={c}
              lines_of={lines_of}
              on_download={on_download_lines}
              on_close={() => setOpenKey('')}
            />
          ) : null}
        </React.Fragment>
      ))}
    </ul>
  );
};

const OnlyGroup = ({ title, rows, note }) =>
  rows.length ? (
    <div className="tp-st-only">
      <div className="tp-st-list-hd">
        {title} <span className="tp-tab-ct">{rows.length}</span>
      </div>
      <p className="tp-st-note">{note}</p>
      <ul className="tp-st-only-list">
        {rows.map((t) => (
          <li key={`${t.group}|${t.teacher_id}`}>
            <Who t={t} />
          </li>
        ))}
      </ul>
    </div>
  ) : null;

/* One row per compared teacher (sortable, expandable), then the teachers only on one side. */
const ByTeacher = ({ teachers, file_name, month, on_download, on_download_lines }) => {
  const lines_of = use_lines(month);
  const [filters, setFilters] = useState({ teacher: '', only_differences: true });
  const [sort, setSort] = useState(DEFAULT_TEACHER_SORT);
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState({});
  const set_filter = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setOffset(0);
  };
  const shown = useMemo(
    () => sort_teachers(filter_teachers(teachers.compared, filters), sort),
    [teachers.compared, filters, sort]
  );
  const page = shown.slice(offset, offset + PAGE_SIZE);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const all_rows = [...shown, ...teachers.only_in_sheet, ...teachers.only_in_ours];

  const columns = [
    {
      key: 'teacher',
      role: 'identifier',
      label: 'Teacher',
      sort_key: 'teacher',
      render: (t) => <Who t={t} />
    },
    {
      key: 'differing',
      label: 'Columns differing',
      align: 'right',
      sort_key: 'differing',
      render: (t) => (
        <span>
          {fmt_count(t.differing)}
          {t.unreadable ? <span className="tp-c-meta"> ({t.unreadable} not a number)</span> : null}
        </span>
      )
    },
    {
      key: 'net',
      label: 'Net difference',
      align: 'right',
      role: 'amount',
      sort_key: 'net',
      // The basis caption tells Ops which bottom line the number is (Net Payable, else a fallback).
      render: (t) => (
        <span title={NET_BASIS_TEXT[t.net_basis] || undefined}>
          {t.net ? (
            <span className={signed_class(t.net)}>{diff_text(t.net, null)}</span>
          ) : (
            <span className="tp-muted">{value_text(0, null)}</span>
          )}
          {t.net_basis ? (
            <span className="tp-c-meta tp-st-net-basis">{NET_BASIS_LABEL[t.net_basis]}</span>
          ) : null}
        </span>
      )
    },
    {
      key: 'worst',
      label: 'Largest differences',
      render: (t) => (
        <div className="tp-st-worst">
          {t.worst.length ? (
            <ul>
              {t.worst.map((c) => (
                <li key={c.key}>{worst_text(c)}</li>
              ))}
            </ul>
          ) : (
            <span className="tp-muted">Exact</span>
          )}
          <button
            type="button"
            className="tp-link"
            aria-expanded={!!open[t.teacher_id]}
            onClick={() => toggle(t.teacher_id)}
          >
            {open[t.teacher_id] ? 'Hide columns' : `Show all ${t.cells.length} columns`}
          </button>
        </div>
      )
    },
    {
      key: 'cells',
      label: 'All columns',
      line: { from: 'teacher', bare: true },
      render: (t) =>
        open[t.teacher_id] ? (
          <TeacherCells t={t} lines_of={lines_of} on_download_lines={on_download_lines} />
        ) : null
    }
  ];

  return (
    <>
      <div className="tp-st-body">
        <FilterBar label="Filter teachers">
          <div className="tp-teacher-search">
            <input
              className="tp-input"
              type="search"
              value={filters.teacher}
              onChange={(e) => set_filter({ teacher: e.target.value })}
              placeholder="Teacher name or ID"
              aria-label="Filter teachers by name or ID"
            />
          </div>
          <CheckChip
            checked={filters.only_differences}
            onChange={() => set_filter({ only_differences: !filters.only_differences })}
          >
            Only teachers with a difference
          </CheckChip>
          <button
            type="button"
            className="tp-btn-out"
            onClick={() => on_download(all_rows)}
            aria-disabled={all_rows.length ? undefined : 'true'}
          >
            ↓ By-teacher CSV
          </button>
        </FilterBar>
        <p className="tp-st-note tp-st-bt-sub">
          {file_name ? `${file_name} · ` : ''}
          {shown.length} {shown.length === 1 ? 'teacher' : 'teachers'} · net is money only (ours −
          sheet), days are counted in Columns differing
        </p>
      </div>
      <DataTable
        columns={columns}
        rows={page}
        rowKey={(t) => t.teacher_id}
        sort={sort}
        on_sort={(key) => {
          setSort((s) => next_teacher_sort(s, key));
          setOffset(0);
        }}
        emptyText={
          filters.only_differences && !filters.teacher
            ? 'Every compared teacher matches (money within ₹1, days exactly).'
            : 'No teachers for these filters.'
        }
        card_below={620}
      />
      <Pager
        offset={offset}
        rows={page.length}
        total={shown.length}
        page_size={PAGE_SIZE}
        on_page={setOffset}
      />
      <div className="tp-st-lists">
        <OnlyGroup
          title={GROUP_LABELS[GROUP_ONLY_SHEET]}
          rows={teachers.only_in_sheet}
          note="In your sheet, but we have no statement row for them this month (not compared)."
        />
        <OnlyGroup
          title={GROUP_LABELS[GROUP_ONLY_OURS]}
          rows={teachers.only_in_ours}
          note="We have a statement row, but your sheet has no row with their teacher_id (not compared)."
        />
      </div>
    </>
  );
};

export default ByTeacher;
